import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Trash2, Save, Loader2, GripVertical, MessageSquare, FileDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { exportConversationPreparationPDF } from "@/lib/conversationPdfExport";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ConversationField {
  key: string;
  label: string;
  placeholder: string;
}

interface ConversationBlock {
  id: string;
  block_id: string;
  block_label: string;
  fields: ConversationField[];
  sort_order: number;
  is_active: boolean;
}

export function ConversationFieldSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [blocks, setBlocks] = useState<ConversationBlock[]>([]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("conversation_field_settings")
        .select("*")
        .order("sort_order");

      if (error) throw error;
      
      // Parse fields from JSONB
      const parsedBlocks = (data || []).map((block) => ({
        ...block,
        fields: typeof block.fields === 'string' ? JSON.parse(block.fields) : block.fields,
      }));
      
      setBlocks(parsedBlocks);
    } catch (error: any) {
      console.error("Error loading settings:", error);
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      for (const block of blocks) {
        const { error } = await supabase
          .from("conversation_field_settings")
          .update({
            block_label: block.block_label,
            fields: JSON.parse(JSON.stringify(block.fields)),
            is_active: block.is_active,
            sort_order: block.sort_order,
          })
          .eq("id", block.id);

        if (error) throw error;
      }

      toast.success("Einstellungen gespeichert");
    } catch (error: any) {
      console.error("Error saving settings:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPreparation = async () => {
    try {
      setExporting(true);
      await exportConversationPreparationPDF();
      toast.success("Vorbereitungsbogen wurde erstellt");
    } catch (error: any) {
      console.error("Error exporting preparation PDF:", error);
      toast.error("Fehler beim Erstellen des PDFs");
    } finally {
      setExporting(false);
    }
  };

  const updateBlockLabel = (blockId: string, label: string) => {
    setBlocks(blocks.map(b => 
      b.id === blockId ? { ...b, block_label: label } : b
    ));
  };

  const updateBlockActive = (blockId: string, isActive: boolean) => {
    setBlocks(blocks.map(b => 
      b.id === blockId ? { ...b, is_active: isActive } : b
    ));
  };

  const updateFieldLabel = (blockId: string, fieldIndex: number, label: string) => {
    setBlocks(blocks.map(b => {
      if (b.id === blockId) {
        const newFields = [...b.fields];
        newFields[fieldIndex] = { ...newFields[fieldIndex], label };
        return { ...b, fields: newFields };
      }
      return b;
    }));
  };

  const updateFieldPlaceholder = (blockId: string, fieldIndex: number, placeholder: string) => {
    setBlocks(blocks.map(b => {
      if (b.id === blockId) {
        const newFields = [...b.fields];
        newFields[fieldIndex] = { ...newFields[fieldIndex], placeholder };
        return { ...b, fields: newFields };
      }
      return b;
    }));
  };

  const addField = (blockId: string) => {
    setBlocks(blocks.map(b => {
      if (b.id === blockId) {
        const newKey = `${b.block_id}_custom_${Date.now()}`;
        return {
          ...b,
          fields: [
            ...b.fields,
            { key: newKey, label: "Neues Feld", placeholder: "Placeholder-Text..." }
          ]
        };
      }
      return b;
    }));
  };

  const removeField = (blockId: string, fieldIndex: number) => {
    setBlocks(blocks.map(b => {
      if (b.id === blockId) {
        const newFields = b.fields.filter((_, i) => i !== fieldIndex);
        return { ...b, fields: newFields };
      }
      return b;
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Mitarbeitergespräche Felder
            </CardTitle>
            <CardDescription>
              Konfigurieren Sie die Blöcke und Felder für Mitarbeitergespräche
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleExportPreparation} 
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              Vorbereitungsbogen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="space-y-4">
            {blocks.map((block) => (
              <AccordionItem 
                key={block.id} 
                value={block.id} 
                className="border rounded-lg px-4"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-4 flex-1">
                    <GripVertical className="h-5 w-5 text-muted-foreground" />
                    <span className={!block.is_active ? "text-muted-foreground" : ""}>
                      {block.block_label}
                    </span>
                    {!block.is_active && (
                      <span className="text-xs text-muted-foreground">(deaktiviert)</span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-4">
                  {/* Block Settings */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex-1 space-y-2 mr-4">
                      <Label>Block-Titel</Label>
                      <Input
                        value={block.block_label}
                        onChange={(e) => updateBlockLabel(block.id, e.target.value)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${block.id}`}>Aktiv</Label>
                      <Switch
                        id={`active-${block.id}`}
                        checked={block.is_active}
                        onCheckedChange={(checked) => updateBlockActive(block.id, checked)}
                      />
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Felder</Label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addField(block.id)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Feld hinzufügen
                      </Button>
                    </div>
                    
                    {block.fields.map((field, fieldIndex) => (
                      <div 
                        key={field.key} 
                        className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 p-3 border rounded-lg"
                      >
                        <div className="space-y-1">
                          <Label className="text-xs">Feldbezeichnung</Label>
                          <Input
                            value={field.label}
                            onChange={(e) => updateFieldLabel(block.id, fieldIndex, e.target.value)}
                            placeholder="Feldbezeichnung"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Placeholder</Label>
                          <Input
                            value={field.placeholder}
                            onChange={(e) => updateFieldPlaceholder(block.id, fieldIndex, e.target.value)}
                            placeholder="Placeholder-Text"
                          />
                        </div>
                        <div className="flex items-end">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Feld löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Möchten Sie dieses Feld wirklich löschen? Bestehende Gespräche behalten ihre Daten, aber das Feld wird für neue Gespräche nicht mehr angezeigt.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => removeField(block.id, fieldIndex)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
